import type { ReactNode } from 'react';
import { Table } from './primitives';
import type { TableColumn } from './primitives';

interface EntityTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  loading?: boolean;
  rowKey?: string | ((r: T) => string);
  page: number;          // 0-based
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onSort?: (field: string) => void;
  sortField?: string;
  sortDir?: 'asc' | 'desc' | string;
  emptyText?: ReactNode;
  size?: 'small' | 'middle' | 'large';
}

/**
 * Standard data table with 0-based pagination + sort controls.
 * Drop-in replacement for the per-feature Table wrappers that used to live
 * in each entity folder (RegionTable, CityTable, etc.).
 */
export default function EntityTable<T = any>({
  columns, data, loading, rowKey = 'id',
  page, pageSize, total, onPageChange,
  onSort, sortField, sortDir,
  emptyText, size = 'middle',
}: EntityTableProps<T>) {
  const wiredColumns = columns.map((col) => {
    if (!col.sorter) return col;
    const field = Array.isArray(col.dataIndex) ? col.dataIndex.join('.') : (col.dataIndex as string);
    return {
      ...col,
      sortOrder: (sortField === field
        ? (sortDir === 'asc' ? 'ascend' : 'descend')
        : null) as 'ascend' | 'descend' | null,
    };
  });

  return (
    <Table
      size={size}
      rowKey={rowKey as any}
      columns={wiredColumns}
      dataSource={data}
      loading={loading}
      locale={{ emptyText }}
      onChange={(_p, _f, sorter: any) => { if (sorter?.field && onSort) onSort(sorter.field); }}
      pagination={{
        current: page + 1,
        pageSize,
        total,
        showSizeChanger: false,
        showTotal: (t: number, range: [number, number]) => `${range[0]}–${range[1]} of ${t}`,
        onChange: (p: number) => onPageChange(p - 1),
        hideOnSinglePage: false,
      }}
    />
  );
}
